import { useTranslation } from 'react-i18next';

import { I18N_ROUTING_ENABLED, type AppLocale } from '../i18n';
import { alternatePaths, localizedPath } from '../i18n/slugMap';

const SITE_URL = 'https://bolaoelite.com.br';

/** Dados de SEO multilíngue prontos para passar ao componente `<SEO>`. */
export interface LocalizedSeo {
  /** Idioma ativo da página (og:locale + <html lang>). */
  locale: AppLocale;
  /** URL canônica da página no idioma ativo. */
  canonical: string;
  /** og:url (igual à canônica). */
  ogUrl: string;
  /**
   * hreflang alternates (pt-BR/en/es + x-default) — `undefined` quando as
   * rotas i18n estão desligadas, pra o `<SEO>` não emitir tags hreflang.
   */
  alternates: Array<{ hreflang: string; href: string }> | undefined;
}

function normalizeLocale(lng: string | undefined): AppLocale {
  if (lng === 'en' || lng === 'es') return lng;
  return 'pt-BR';
}

/**
 * Deriva canonical, og:url, og:locale e hreflang de uma página pública a
 * partir do seu `routeId` (chave de `ROUTE_SLUGS`) e do idioma ativo.
 *
 * Com a flag `I18N_ROUTING_ENABLED` OFF (default), retorna sempre a URL
 * PT-BR sem alternates — comportamento idêntico ao de antes da Release C,
 * zero regressão. Com a flag ON, resolve o idioma corrente e devolve a URL
 * localizada + o conjunto completo de hreflang.
 */
export function useLocalizedSeo(routeId: string): LocalizedSeo {
  const { i18n } = useTranslation();

  if (!I18N_ROUTING_ENABLED) {
    const ptUrl = `${SITE_URL}${localizedPath(routeId, 'pt-BR')}`;
    return { locale: 'pt-BR', canonical: ptUrl, ogUrl: ptUrl, alternates: undefined };
  }

  const locale = normalizeLocale(i18n.language);
  const url = `${SITE_URL}${localizedPath(routeId, locale)}`;
  return { locale, canonical: url, ogUrl: url, alternates: alternatePaths(routeId) };
}
